import { useEffect, useRef, useState } from "react";
import { ApiError, type CorpusGap, type Proposal, api, label } from "../api.js";

/**
 * Кадр 7 на живых данных. Лента одна на обе поверхности: реплики из Telegram видны
 * здесь же и помечены поверхностью. Под ответом агента показан провенанс — какими
 * инструментами он считал и сколько чисел осталось без источника.
 *
 * Этап 5 добавил карточки предложений. Это единственный способ агента что-то изменить:
 * он кладёт карточку, а в леджер она попадает нажатием владельца. Кнопка «принять»
 * не даёт обходных путей — правила кода при применении работают как обычно, и протокол
 * сверх лимита так же упрётся в 409.
 */

type Message = { role: string; surface: string; content: string; at: string; kind: string | null };

/**
 * Состояние инициативы: почему бот сейчас молчит и что из его непрошеных сообщений
 * вышло. Второе — единственный честный ответ на «помогает ли он вообще»; первое нужно,
 * чтобы молчание не путалось с поломкой. Проценты здесь не показываются намеренно:
 * при n порядка десятка процент — украшение, а не измерение.
 */
type Initiative = {
  budget: {
    budget: number;
    spent_today: number;
    left: number;
    quiet_hours: boolean;
    allowed: boolean;
    reason: string | null;
  };
  stats: { n: number; replied: number; dismissed: number; ignored: number; led_to_touch: number };
  queue: { pending: number; dead: number };
  /** Денежный потолок — тот же вопрос, что и бюджет инициативы: сколько бот себе позволяет. */
  spend: { day_usd: number; month_usd: number; day_limit: number; month_limit: number; allowed: boolean };
};

type TurnResult = {
  answer: string;
  toolCalls: { name: string }[];
  provenance: { numbers: number[]; ungrounded: number[]; ok: boolean };
  usage: { cost_usd: number };
};

export function ChatScreen(): React.JSX.Element {
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastTurn, setLastTurn] = useState<TurnResult | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [gaps, setGaps] = useState<CorpusGap[]>([]);
  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  async function reload() {
    const [chat, pending, nudges] = await Promise.all([
      api.get<{ messages: Message[] }>("/api/messages"),
      api.get<{ proposals: Proposal[]; corpus_gaps: CorpusGap[] }>("/api/proposals"),
      api.get<Initiative>("/api/nudges")
    ]);
    setMessages(chat.messages);
    setProposals(pending.proposals);
    setGaps(pending.corpus_gaps);
    setInitiative(nudges);
  }

  async function decide(id: string, action: "apply" | "dismiss") {
    setError(null);
    try {
      await api.post(`/api/proposals/${id}/${action}`);
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не удалось применить");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  async function send() {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<TurnResult>("/api/agent/turn", { text, surface: "miniapp" });
      setLastTurn(result);
      setText("");
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "агент не ответил");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="head">
        <h1>Чат</h1>
        <span className="date">одна лента на обе поверхности</span>
      </header>
      <p className="subhead">
        Реплики из Telegram видны здесь же. Агент не пишет в цели напрямую: всё, что он
        предлагает, ждёт вашего нажатия.
      </p>

      {initiative && <InitiativeNote state={initiative} />}

      {messages.length === 0 && <p className="empty">Разговор ещё не начинался.</p>}

      {messages.map((message, index) => (
        <div className={`msg ${message.role === "owner" ? "me" : ""}`} key={`${message.at}-${index}`}>
          {message.role !== "owner" && (
            <span className="av">
              <Spark />
            </span>
          )}
          <div className="bub">
            {message.role === "owner" && message.surface === "telegram" && (
              <span className="init">из Telegram</span>
            )}
            {/* Непрошеное сообщение помечено: «он мне ответил» и «он сам заговорил» —
                разные вещи, а роль их не различает. */}
            {message.kind === "nudge" && <span className="init">сам заговорил</span>}
            {message.kind === "reminder" && <span className="init">напоминание</span>}
            {message.content}
          </div>
        </div>
      ))}

      {lastTurn && (
        <div className="prov">
          <span className="k">провенанс</span>
          <span>
            инструменты: {lastTurn.toolCalls.map((call) => call.name).join(", ") || "не вызывались"} · чисел{" "}
            {lastTurn.provenance.numbers.length}, без источника{" "}
            <b className={lastTurn.provenance.ok ? "" : "miss-mark"}>{lastTurn.provenance.ungrounded.length}</b> · $
            {lastTurn.usage.cost_usd}
          </span>
        </div>
      )}

      {proposals.length > 0 && (
        <>
          <div className="sec">Предложения агента</div>
          {/* Одна панель на все предложения: два стеклянных слоя подряд выбивали кадр
              «Чат» за лимит в четыре. Разделяются они волосяной чертой, а не воздухом. */}
          <section className="props">
          {proposals.map((proposal) => (
            <div className="prop" key={proposal.id}>
              <div className="prop-head">
                <span className="chip axis">{label(proposal.kind)}</span>
                <span className="lock">в леджере пока нет</span>
              </div>
              {proposal.rationale && <p className="prop-why">{proposal.rationale}</p>}
              <PayloadSummary kind={proposal.kind} payload={proposal.parsed} />
              <div className="choices">
                <button className="btn primary" onClick={() => void decide(proposal.id, "apply")}>
                  Принять
                </button>
                <button className="btn ghost" onClick={() => void decide(proposal.id, "dismiss")}>
                  Отклонить
                </button>
              </div>
            </div>
          ))}
          </section>
        </>
      )}

      {gaps.length > 0 && (
        <>
          <div className="sec">Дырки в корпусе</div>
          <div className="card">
            <p className="lock">
              Агент не нашёл методику под задачу и не стал выдумывать. Корпус пополняется
              только оффлайн и ревьюится в git.
            </p>
            {gaps.map((gap) => (
              <p className="gap-note" key={gap.id}>
                «{gap.query}»{gap.reason ? ` — ${gap.reason}` : ""}
              </p>
            ))}
          </div>
        </>
      )}

      {error && <p className="error">{error}</p>}
      {/* Место под строку ввода: она отклеена от края и стоит НАД панелью вкладок,
          поэтому нижнего поля страницы ей не хватает. */}
      <div className="chat-tail" ref={bottom} />

      <div className="composer">
        <div className="bar-row">
          <input
            className="field"
            value={text}
            placeholder="Написать агенту…"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void send();
            }}
          />
          <button className="send" disabled={busy} onClick={() => void send()} aria-label="Отправить">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 19V5" />
              <path d="M6 11l6-6 6 6" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}

/** Аватар агента — та же искра, что и на вкладке «Оракул»: один знак на одну роль. */
function Spark(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l2.2 6.1L20.5 11l-6.3 1.9L12 19l-2.2-6.1L3.5 11l6.3-1.9z" />
    </svg>
  );
}

/** Что именно предлагается — без JSON в лицо владельцу. */
function PayloadSummary({ kind, payload }: { kind: string; payload: Record<string, unknown> }): React.JSX.Element {
  const get = (key: string) => (payload[key] === undefined ? null : String(payload[key]));

  if (kind === "goal") {
    const rule = payload.machine_rule as { metric: string; op: string; value: number; unit?: string } | undefined;
    return (
      <dl className="prop-body">
        <dt>цель</dt>
        <dd>{get("title")}</dd>
        <dt>оси</dt>
        {/* Через label(): «project · month · personal» — машинные ключи в тексте,
            который читает владелец, а не разработчик. */}
        <dd>
          {label(get("goal_type") ?? "")} · {label(get("horizon_bucket") ?? "")} · {label(get("domain") ?? "")}
          {get("target_date") ? ` · к ${get("target_date")}` : ""}
        </dd>
        <dt>критерий</dt>
        <dd>{get("success_criterion")}</dd>
        <dt>доказательство</dt>
        <dd>{get("proof_rule")}</dd>
        {/* Правило показывается отдельной строкой: именно оно решает, кто будет судить —
            код или модель. Владелец должен видеть это до нажатия, а не после вердикта. */}
        <dt>судит</dt>
        <dd>
          {rule
            ? `код: ${rule.metric} ${rule.op} ${rule.value}${rule.unit ? ` ${rule.unit}` : ""}`
            : "модель по качественному правилу — числового критерия нет"}
        </dd>
      </dl>
    );
  }
  if (kind === "protocol") {
    return (
      <dl className="prop-body">
        <dt>гипотеза</dt>
        <dd>{get("hypothesis")}</dd>
        {get("method_id") && (
          <>
            <dt>методика</dt>
            <dd>{get("method_id")}</dd>
          </>
        )}
        <dt>метрика</dt>
        <dd>
          {get("metric_name")} · окно {get("window_start")} — {get("window_end")}
        </dd>
        <dt>правило успеха</dt>
        <dd>{get("success_rule")}</dd>
      </dl>
    );
  }
  if (kind === "criterion_change") {
    return (
      <dl className="prop-body">
        {get("success_criterion") && (
          <>
            <dt>критерий</dt>
            <dd>{get("success_criterion")}</dd>
          </>
        )}
        {get("proof_rule") && (
          <>
            <dt>правило доказательства</dt>
            <dd>{get("proof_rule")}</dd>
          </>
        )}
        <dt>почему</dt>
        <dd>{get("reason")}</dd>
      </dl>
    );
  }
  if (kind === "method_match") {
    return (
      <dl className="prop-body">
        <dt>методика</dt>
        <dd>{get("method_id")}</dd>
        <dt>зачем</dt>
        <dd>{get("why")}</dd>
      </dl>
    );
  }
  return (
    <dl className="prop-body">
      <dt>название</dt>
      <dd>{get("title")}</dd>
    </dl>
  );
}

/**
 * Блок инициативы: ворота и счётчики.
 *
 * Молчание бота обязано быть объяснимым. Иначе «он ничего не пишет» неотличимо от
 * «он сломался», а из граблей проекта известно, чем это кончается: бот, который надоел,
 * уходит в mute вместе со всеми напоминаниями, и заметить момент невозможно.
 *
 * Счётчики идут голыми числами со своим n — без процентов. «После 4 было касание»
 * при n = 12 честно; «33%» при том же n выглядело бы измерением, которым не является.
 */
function InitiativeNote({ state }: { state: Initiative }): React.JSX.Element {
  const { budget, stats, queue } = state;
  return (
    <p className="state-note">
      {budget.allowed ? (
        <>
          <b>Может заговорить.</b> Сегодня потрачено {budget.spent_today} из {budget.budget}.
        </>
      ) : (
        <>
          <b>Молчит:</b> {budget.reason ?? "поводов нет"}.
        </>
      )}
      <span className="counts">
        {stats.n === 0
          ? "Сам ещё не заговаривал ни разу — считать нечего, и это честнее нуля процентов."
          : `Заговорил сам ${stats.n} · ответили ${stats.replied} · отмахнулись ${stats.dismissed} · промолчали ${stats.ignored} · после ${stats.led_to_touch} было касание`}
      </span>
      <span className="counts">
        {budget.allowed || state.spend.allowed
          ? `Модель: $${state.spend.day_usd.toFixed(2)} сегодня из $${state.spend.day_limit.toFixed(2)} · $${state.spend.month_usd.toFixed(2)} за месяц из $${state.spend.month_limit.toFixed(2)}`
          : `Потолок расхода исчерпан: $${state.spend.day_usd.toFixed(2)} за сегодня. Леджер и аналитика работают без модели.`}
      </span>
      {queue.dead > 0 && <span className="counts">Джобов застряло: {queue.dead} — часть сообщений могла не уйти.</span>}
    </p>
  );
}
